import React from 'react';
import './LegalPage.css';

interface PrivacyPolicyProps {
    onClose: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onClose }) => {
    return (
        <div className="legal-overlay" onClick={onClose}>
            <div className="legal-container" onClick={(e) => e.stopPropagation()}>
                <button className="legal-close-btn" onClick={onClose} aria-label="Close">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="legal-content">
                    <h1>Privacy Policy</h1>
                    <p className="legal-updated">Last Updated: February 15, 2026</p>

                    <section>
                        <h2>1. Introduction</h2>
                        <p>
                            Welcome to CensusLens ("we," "our," or "us"). We are committed to protecting your privacy and ensuring transparency about how we collect, use, and share information when you use our demographic intelligence platform.
                        </p>
                    </section>

                    <section>
                        <h2>2. Information We Collect</h2>
                        <h3>2.1 Information You Provide</h3>
                        <p>
                            CensusLens does not require user registration or account creation. We do not collect personal information such as names, email addresses, or contact details unless you voluntarily provide them through contact forms or support channels.
                        </p>

                        <h3>2.2 Automatically Collected Information</h3>
                        <p>When you visit CensusLens, we may automatically collect:</p>
                        <ul>
                            <li><strong>Usage Data:</strong> Information about how you interact with our platform, including pages viewed, features used, and time spent on the site.</li>
                            <li><strong>Device Information:</strong> Browser type, operating system, IP address, and device identifiers.</li>
                            <li><strong>Location Data:</strong> Approximate geographic location based on IP address (not precise GPS coordinates).</li>
                            <li><strong>Cookies and Similar Technologies:</strong> We use cookies and local storage to enhance your experience and remember your preferences.</li>
                        </ul>
                    </section>

                    <section>
                        <h2>3. How We Use Your Information</h2>
                        <p>We use collected information to:</p>
                        <ul>
                            <li>Provide, maintain, and improve CensusLens services</li>
                            <li>Analyze usage patterns and optimize user experience</li>
                            <li>Detect, prevent, and address technical issues</li>
                            <li>Comply with legal obligations</li>
                            <li>Display relevant advertisements through Google AdSense</li>
                        </ul>
                    </section>

                    <section>
                        <h2>4. Third-Party Services</h2>
                        <h3>4.1 Google AdSense</h3>
                        <p>
                            CensusLens uses Google AdSense to display advertisements. Google may use cookies and web beacons to serve ads based on your prior visits to our website or other websites. You can opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google's Ads Settings</a>.
                        </p>

                        <h3>4.2 Analytics Services</h3>
                        <p>
                            We may use analytics services (such as Google Analytics) to understand how users interact with CensusLens. These services may collect information about your use of our platform and other websites.
                        </p>

                        <h3>4.3 U.S. Census Bureau Data</h3>
                        <p>
                            All demographic data displayed on CensusLens is sourced from publicly available U.S. Census Bureau datasets. We do not collect or store individual-level census responses.
                        </p>
                    </section>

                    <section>
                        <h2>5. Cookies and Tracking Technologies</h2>
                        <p>We use the following types of cookies:</p>
                        <ul>
                            <li><strong>Essential Cookies:</strong> Required for the platform to function properly</li>
                            <li><strong>Preference Cookies:</strong> Remember your settings and choices</li>
                            <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                            <li><strong>Advertising Cookies:</strong> Used by Google AdSense to display relevant ads</li>
                        </ul>
                        <p>
                            You can control cookies through your browser settings. Note that disabling certain cookies may limit functionality.
                        </p>
                    </section>

                    <section>
                        <h2>6. Data Sharing and Disclosure</h2>
                        <p>We do not sell, trade, or rent your personal information. We may share information with:</p>
                        <ul>
                            <li><strong>Service Providers:</strong> Third parties who assist in operating our platform (e.g., hosting providers, analytics services)</li>
                            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                        </ul>
                    </section>

                    <section>
                        <h2>7. Data Security</h2>
                        <p>
                            We implement reasonable security measures to protect information from unauthorized access, alteration, disclosure, or destruction. However, no internet transmission is completely secure, and we cannot guarantee absolute security.
                        </p>
                    </section>

                    <section>
                        <h2>8. Children's Privacy</h2>
                        <p>
                            CensusLens is not intended for children under 13 years of age. We do not knowingly collect personal information from children. If you believe we have inadvertently collected such information, please contact us immediately.
                        </p>
                    </section>

                    <section>
                        <h2>9. Your Rights and Choices</h2>
                        <p>Depending on your location, you may have the right to:</p>
                        <ul>
                            <li>Access the information we hold about you</li>
                            <li>Request correction or deletion of your information</li>
                            <li>Opt out of certain data collection practices</li>
                            <li>Withdraw consent where processing is based on consent</li>
                        </ul>
                        <p>
                            To exercise these rights, please contact us using the information in the Contact section below.
                        </p>
                    </section>

                    <section>
                        <h2>10. International Data Transfers</h2>
                        <p>
                            CensusLens is operated in the United States. If you access our platform from outside the U.S., your information may be transferred to, stored, and processed in the U.S. or other countries where our service providers operate.
                        </p>
                    </section>

                    <section>
                        <h2>11. Changes to This Privacy Policy</h2>
                        <p>
                            We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of CensusLens after changes constitutes acceptance of the updated policy.
                        </p>
                    </section>

                    <section>
                        <h2>12. Contact Us</h2>
                        <p>If you have questions or concerns about this Privacy Policy, please contact us at:</p>
                        <p className="contact-info">
                            <strong>Email:</strong> censuslensapp@gmail.com<br />
                            <strong>Website:</strong> https://censuslens.com
                        </p>
                    </section>

                    <section className="legal-footer">
                        <p>
                            By using CensusLens, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
